import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Clock,
  FileText,
  Check,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  Info,
} from 'lucide-react';

import JobConfirmationTemplateSettings from './JobConfirmationTemplateSettings';

const NotificationsSettingsTab = () => {
  const { api } = useAuth();
  const { fmtDateTime } = useTenantSettings();
  const [subTab, setSubTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [availableVars, setAvailableVars] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logSearch, setLogSearch] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/notification-templates');
      setTemplates(res.data.templates);
      setAvailableVars(res.data.available_variables);
    } catch (err) {
      toast.error('Failed to load templates');
    }
  }, [api]);

  const fetchLogs = useCallback(async (offset = 0) => {
    try {
      const res = await api.get('/notification-log', { params: { limit: 20, offset } });
      setLogs(res.data.logs);
      setLogTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load notification log');
    }
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchLogs()]);
      setLoading(false);
    };
    load();
  }, [fetchTemplates, fetchLogs]);

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setShowEditDialog(true);
  };

  const handleNewTemplate = () => {
    setEditingTemplate({
      id: null,
      name: '',
      title_template: '',
      message_template: '',
      notification_type: 'message',
      is_active: true,
      is_system: false,
    });
    setShowEditDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate.name || !editingTemplate.title_template || !editingTemplate.message_template) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      if (editingTemplate.id) {
        await api.put(`/notification-templates/${editingTemplate.id}`, {
          name: editingTemplate.name,
          title_template: editingTemplate.title_template,
          message_template: editingTemplate.message_template,
          notification_type: editingTemplate.notification_type,
          is_active: editingTemplate.is_active,
        });
        toast.success('Template updated');
      } else {
        await api.post('/notification-templates', {
          name: editingTemplate.name,
          title_template: editingTemplate.title_template,
          message_template: editingTemplate.message_template,
          notification_type: editingTemplate.notification_type,
          is_active: editingTemplate.is_active,
        });
        toast.success('Template created');
      }
      setShowEditDialog(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Delete "${template.name}"?`)) return;
    try {
      await api.delete(`/notification-templates/${template.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/notification-templates/${template.id}`, {
        is_active: !template.is_active,
      });
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const handleLogPageChange = (newPage) => {
    setLogPage(newPage);
    fetchLogs(newPage * 20);
  };

  const insertVariable = (varKey) => {
    if (!editingTemplate) return;
    const placeholder = `{${varKey}}`;
    setEditingTemplate(prev => ({
      ...prev,
      message_template: prev.message_template + placeholder
    }));
  };

  const filteredLogs = logSearch
    ? logs.filter(l =>
        l.operator_name?.toLowerCase().includes(logSearch.toLowerCase()) ||
        l.template_name?.toLowerCase().includes(logSearch.toLowerCase()) ||
        l.message?.toLowerCase().includes(logSearch.toLowerCase()) ||
        l.sent_by?.toLowerCase().includes(logSearch.toLowerCase())
      )
    : logs;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-settings-tab">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="subtab-templates">
            <FileText className="h-4 w-4 mr-1.5" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="subtab-log">
            <Clock className="h-4 w-4 mr-1.5" />
            Notification Log
          </TabsTrigger>
        </TabsList>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Push Notification Templates</h3>
              <p className="text-sm text-muted-foreground">Customise message templates for operator push notifications</p>
            </div>
            <Button onClick={handleNewTemplate} data-testid="create-template-button">
              <Plus className="h-4 w-4 mr-1.5" />
              New Template
            </Button>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Message Preview</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map(t => (
                  <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {t.name}
                        {t.is_system && (
                          <Badge variant="secondary" className="text-[10px]">System</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.title_template}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-[300px] truncate">
                      {t.message_template}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={t.is_active}
                        onCheckedChange={() => handleToggleActive(t)}
                        data-testid={`template-toggle-${t.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(t)} data-testid={`edit-template-${t.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {!t.is_system && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(t)} className="text-destructive" data-testid={`delete-template-${t.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No templates found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Variable Reference */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="h-4 w-4 text-[#ffd108]" />
                Available Variables
              </CardTitle>
              <CardDescription className="text-xs">
                Use these placeholders in your templates. They will be replaced with actual values when sending.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {availableVars.map(v => (
                  <Badge key={v.key} variant="outline" className="text-xs font-mono cursor-default" title={`Example: ${v.example}`}>
                    {`{${v.key}}`} — {v.label}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Job Confirmation Email Template */}
          <JobConfirmationTemplateSettings />
        </TabsContent>

        {/* NOTIFICATION LOG TAB */}
        <TabsContent value="log" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Notification History</h3>
              <p className="text-sm text-muted-foreground">Log of all push notifications sent to operators</p>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by operator, template..."
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                className="pl-9"
                data-testid="log-search-input"
              />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sent At</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead className="hidden md:table-cell">Message</TableHead>
                  <TableHead>Sent By</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map(log => (
                  <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {fmtDateTime(log.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{log.template_name}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{log.operator_name}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-[250px] truncate">
                      {log.message}
                    </TableCell>
                    <TableCell className="text-sm">{log.sent_by}</TableCell>
                    <TableCell>
                      {log.push_success ? (
                        <Badge className="bg-green-500/20 text-green-500 text-xs">
                          <Check className="h-3 w-3 mr-1" />
                          Sent
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-500 text-xs">
                          <X className="h-3 w-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {logSearch ? 'No matching notifications found' : 'No notifications sent yet'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {logTotal > 20 && (
            <div className="flex items-center justify-between px-2">
              <p className="text-sm text-muted-foreground">
                Showing {logPage * 20 + 1}-{Math.min((logPage + 1) * 20, logTotal)} of {logTotal}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={logPage === 0} onClick={() => handleLogPageChange(logPage - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={(logPage + 1) * 20 >= logTotal} onClick={() => handleLogPageChange(logPage + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create Template Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.id ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogDescription>
              {editingTemplate?.id ? 'Modify the notification template.' : 'Create a new notification template for your team.'}
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={e => setEditingTemplate(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Job Assigned"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Push Title</Label>
                <Input
                  value={editingTemplate.title_template}
                  onChange={e => setEditingTemplate(p => ({ ...p, title_template: e.target.value }))}
                  placeholder="e.g. New Job Assigned"
                  data-testid="template-title-input"
                />
              </div>
              <div>
                <Label>Message Body</Label>
                <Textarea
                  value={editingTemplate.message_template}
                  onChange={e => setEditingTemplate(p => ({ ...p, message_template: e.target.value }))}
                  placeholder="e.g. You've been assigned to Job #{job_number} at {site_name}"
                  rows={3}
                  data-testid="template-message-input"
                />
                <div className="flex flex-wrap gap-1 mt-2">
                  {availableVars.map(v => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-[#ffd108]/30 text-[#ffd108] hover:bg-[#ffd108]/10 transition-colors font-mono"
                      title={`Insert {${v.key}} — Example: ${v.example}`}
                    >
                      {`{${v.key}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={editingTemplate.notification_type}
                  onValueChange={v => setEditingTemplate(p => ({ ...p, notification_type: v }))}
                >
                  <SelectTrigger data-testid="template-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="message">Message</SelectItem>
                    <SelectItem value="job_assigned">Job Assigned</SelectItem>
                    <SelectItem value="job_changed">Job Changed</SelectItem>
                    <SelectItem value="job_cancelled">Job Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTemplate.is_active}
                  onCheckedChange={v => setEditingTemplate(p => ({ ...p, is_active: v }))}
                  data-testid="template-active-switch"
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={saving} data-testid="save-template-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Check className="h-4 w-4 mr-1.5" />}
              {editingTemplate?.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsSettingsTab;
